import React, { useRef, useEffect, useState, useMemo } from "react";
import CanvasRenderer from "../components/CanvasRenderer";
import CanvasPreview from "../components/CanvasPreview";
import api from "../api";
import { getImage, cacheImage } from "../ImageCache";


const CELL_SIZE = 122;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 4;
const NATIVE_CARD_SIZE = 488; // Native resolution of card background images

function getHazeColor(color) {
  switch (color) {
    case 'red': return 'rgba(200, 0, 0, 0.65)'; // reference to a Conflict that needs to be resolved in the adventure
    case 'green': return 'rgba(0, 200, 0, 0.65)'; // reference to an Aspect that describes a part of the adventure
    case 'blue': return 'rgba(0, 75, 200, 0.65)'; // reference to an Anchor that plays a role in the adventure
    case 'purple': return 'rgba(150, 0, 200, 0.75)'; // reference to an Engine that drives the adventure
    case 'yellow': return 'rgba(200, 150, 0, 0.65)'; // reference to an Agent that plays a role in the adventure
    case 'grey': return 'rgba(60, 60, 60, 0.75)'; // the title of the adventure
    default: return 'transparent';		// the description of the adventure
  }
}

// Get the best src for an image, using the shared cache when available.
// When a cached HTMLImageElement exists, its src can be reused by the browser
// without triggering a new network request.
function cachedImgSrc(url, imageCache) {
  if (imageCache && url) {
    const cached = getImage(url);
    if (cached) return cached.src;
  }
  return url;
}

export default function GridCanvasRenderer({
  cards = [],
  showImages,
  selectedCardIds = [], // Multi-selection
  selectedTextOption = {},
  onSelectCard,
  onSelectTextOption,
  gridOffset = { x: 0, y: 0 },
  zoom = 1,
  onZoomChange,
  onMoveCard,
  onMoveSelectedCards, // Group movement
  onGridOffsetChange,
  language,
  onContainerSizeChange,
  symbols = [],
  decks = [],
  cardTypes = [],
  relationPreviewPath = null,
  relationPreviewColor = '#ff0000',
  relations = [],
  onRelationClick,
  selectedRelation,
  // Box selection props
  isBoxSelecting = false,
  boxSelectionStart = null,
  boxSelectionEnd = null,
  onBoxSelectionStart,
  onBoxSelectionUpdate,
  onBoxSelectionEnd,
  onUserTextChange,
  userCanvases,
  backgroundImage = 'default',
  style = {},
  imageCache = null,   // Map<url, HTMLImageElement> — shared cache to avoid re-fetching during export
  isExportMode = false, // When true: background images render at native 488px, no zoom scaling for backgrounds
}) {
  // Debug: Log the symbols array and symbol 3 lookups
  const containerRef = useRef(null);
  const [draggingCardId, setDraggingCardId] = useState(null);
  const [dragOverCell, setDragOverCell] = useState(null); // {cellX, cellY}
  const [containerSize, setContainerSize] = useState({ width: 800, height: 800 });
  const panStartRef = useRef(null); // {mouseX, mouseY, startX, startY}
  const isPanningRef = useRef(false);
  const [relationPopup, setRelationPopup] = useState(null); // {x, y, matches: [relations]}
  // Box selection state
  const [isDraggingSelection, setIsDraggingSelection] = useState(false);
  const [dragSelectionStart, setDragSelectionStart] = useState(null);
  const [dragSelectionOffset, setDragSelectionOffset] = useState({ x: 0, y: 0 });
  const dragSelectionOffsetRef = useRef({ x: 0, y: 0 });
  // Box selection state
  const [isBoxDragging, setIsBoxDragging] = useState(false);
  const [boxDragStart, setBoxDragStart] = useState(null);
  const ignoreNextClickRef = useRef(false);
  // Add state to track move in progress (so component re-renders when it changes)
  const [isMoveInProgress, setIsMoveInProgress] = useState(false);

  // Build grid map using pos_x and pos_y
  const gridMap = {};
  for (const card of cards) {
    gridMap[`${card.pos_x},${card.pos_y}`] = card;
  }

  // Track container size for virtualized grid
  useEffect(() => {
    if (!containerRef.current) return;
    const updateSize = () => {
      if (containerRef.current) {
        setContainerSize({
          width: containerRef.current.offsetWidth,
          height: containerRef.current.offsetHeight
        });
      }
    };
    updateSize();
    const observer = new window.ResizeObserver(updateSize);
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Calculate visible grid range based on gridOffset and container size
  const buffer = 2; // Render extra cells/cards outside the visible area
  const halfCols = Math.ceil(containerSize.width / (2 * CELL_SIZE * zoom)) + buffer;
  const halfRows = Math.ceil(containerSize.height / (2 * CELL_SIZE * zoom)) + buffer;
  const centerCellX = Math.round(-gridOffset.x / (CELL_SIZE * zoom));
  const centerCellY = Math.round(-gridOffset.y / (CELL_SIZE * zoom));
  const cells = [];
  for (let gx = -halfCols; gx <= halfCols; gx++) {
    for (let gy = -halfRows; gy <= halfRows; gy++) {
      const cellX = centerCellX + gx;
      const cellY = centerCellY + gy;
      const card = gridMap[`${cellX},${cellY}`];
      cells.push({ cellX, cellY, card });
    }
  }

  // Handle mouse wheel for zoom
  useEffect(() => {
    const handleWheel = (e) => {
      if (e.ctrlKey || e.metaKey) return; // Let browser handle pinch-zoom
      if (containerRef.current && containerRef.current.contains(e.target)) {
        e.preventDefault();

        // Calculate mouse position relative to container
        const rect = containerRef.current.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        // Calculate current zoom level
        let newZoom = zoom - e.deltaY * 0.001;
        newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, newZoom));

        // Calculate zoom factor
        const zoomFactor = newZoom / zoom;

        // Calculate mouse position in grid coordinates before zoom
        const oldGridX = (mouseX - containerSize.width / 2 - gridOffset.x) / (CELL_SIZE * zoom);
        const oldGridY = (mouseY - containerSize.height / 2 - gridOffset.y) / (CELL_SIZE * zoom);

        // Calculate new grid offset to keep mouse position centered
        const newGridOffsetX = mouseX - containerSize.width / 2 - (oldGridX * CELL_SIZE * newZoom);
        const newGridOffsetY = mouseY - containerSize.height / 2 - (oldGridY * CELL_SIZE * newZoom);

        // Update zoom and grid offset
        if (onZoomChange) onZoomChange(newZoom);
        if (onGridOffsetChange) onGridOffsetChange({ x: newGridOffsetX, y: newGridOffsetY });
      }
    };
    window.addEventListener('wheel', handleWheel, { passive: false });
    return () => window.removeEventListener('wheel', handleWheel);
  }, [zoom, gridOffset, containerSize, onZoomChange, onGridOffsetChange]);

  // Pan handlers for background/empty cell
  const handlePanMouseDown = e => {
    if (e.button !== 1) return; // Only middle mouse button
    isPanningRef.current = true;
    panStartRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      startX: gridOffset.x,
      startY: gridOffset.y
    };
    window.addEventListener('mousemove', handlePanMove);
    window.addEventListener('mouseup', handlePanEnd, { once: true });
  };
  const handlePanMove = e => {
    if (!isPanningRef.current || !panStartRef.current) return;
    const newOffset = {
      x: panStartRef.current.startX + (e.clientX - panStartRef.current.mouseX),
      y: panStartRef.current.startY + (e.clientY - panStartRef.current.mouseY)
    };
    if (onGridOffsetChange) onGridOffsetChange(newOffset);
  };
  const handlePanEnd = () => {
    isPanningRef.current = false;
    panStartRef.current = null;
    window.removeEventListener('mousemove', handlePanMove);
  };

  // Box selection handlers
  const handleMouseDown = e => {
    if (e.button === 1) {
      // Middle mouse button - handle panning
      handlePanMouseDown(e);
      return;
    }

    if (e.button !== 0) return; // Only left mouse button for selection

    const rect = containerRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    // Convert to grid coordinates using Math.floor for initial click
    const cellX = Math.floor((mouseX - containerSize.width / 2 - gridOffset.x) / (CELL_SIZE * zoom));
    const cellY = Math.floor((mouseY - containerSize.height / 2 - gridOffset.y) / (CELL_SIZE * zoom));

    const clickedCard = gridMap[`${cellX},${cellY}`];

    if (clickedCard) {
      // Clicked on a card
      const isCtrlClick = e.ctrlKey || e.metaKey;
      const isAltClick = e.altKey;
      onSelectCard && onSelectCard(clickedCard, isCtrlClick, isAltClick);

      // Start dragging if it's a selected card or if it is an Alt click
      if (isAltClick || selectedCardIds.includes(clickedCard.canvas_card_id)) {
        setIsDraggingSelection(true);
        setDragSelectionStart({ x: mouseX, y: mouseY });
        setDragSelectionOffset({ x: 0, y: 0 });
      }
    } else {
      // Clicked on empty space - start box selection immediately for visual feedback
      setBoxDragStart({ x: cellX, y: cellY });
      onBoxSelectionStart && onBoxSelectionStart({ x: cellX, y: cellY });
      onBoxSelectionUpdate && onBoxSelectionUpdate({ x: cellX, y: cellY }); // Ensure parent gets initial end value too
    }
  };

  const handleMouseMove = e => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    // Convert to grid coordinates using Math.floor for consistent cell detection
    const cellX = Math.floor((mouseX - containerSize.width / 2 - gridOffset.x) / (CELL_SIZE * zoom));
    const cellY = Math.floor((mouseY - containerSize.height / 2 - gridOffset.y) / (CELL_SIZE * zoom));
    if (isDraggingSelection && dragSelectionStart) {
      const offsetX = mouseX - dragSelectionStart.x;
      const offsetY = mouseY - dragSelectionStart.y;
      dragSelectionOffsetRef.current = { x: offsetX, y: offsetY };
      setDragSelectionOffset({ x: offsetX, y: offsetY });
      setDragOverCell({ cellX, cellY });
    } else if (isBoxSelecting && !isBoxDragging) {
      // Check if we've moved enough to start box dragging
      const startX = boxDragStart.x * CELL_SIZE * zoom + containerSize.width / 2 + gridOffset.x;
      const startY = boxDragStart.y * CELL_SIZE * zoom + containerSize.height / 2 + gridOffset.y;
      const distance = Math.sqrt((mouseX - startX) ** 2 + (mouseY - startY) ** 2);

      if (distance > 5) { // Start dragging after 5px movement
        setIsBoxDragging(true);
        onBoxSelectionUpdate && onBoxSelectionUpdate({ x: cellX, y: cellY });
      } else {
        // Still updating box selection even if not dragging yet
        onBoxSelectionUpdate && onBoxSelectionUpdate({ x: cellX, y: cellY });
      }
    } else if (isBoxDragging) {
      // Always update box selection while dragging
      onBoxSelectionUpdate && onBoxSelectionUpdate({ x: cellX, y: cellY });
    }
  };

  const handleMouseUp = e => {
    if (e.button !== 0) return; // Only left mouse button
    if (isDraggingSelection && dragSelectionStart) {
      // Use the ref value for the latest offset
      const offset = dragSelectionOffsetRef.current;
      const cellOffsetX = Math.round(offset.x / (CELL_SIZE * zoom));
      const cellOffsetY = Math.round(offset.y / (CELL_SIZE * zoom));
      if ((cellOffsetX !== 0 || cellOffsetY !== 0) && onMoveSelectedCards) {
        setIsMoveInProgress(true);
        onMoveSelectedCards(cellOffsetX, cellOffsetY).finally(() => {
          setIsMoveInProgress(false);
        }).catch((error) => {
          // If move fails, reset the move state immediately
          setIsMoveInProgress(false);
        });
      }
      // Always reset drag offset when move is complete
      setDragSelectionOffset({ x: 0, y: 0 });
      dragSelectionOffsetRef.current = { x: 0, y: 0 };
      setIsDraggingSelection(false);
      setDragSelectionStart(null);
      setDragSelectionOffset({ x: 0, y: 0 });
      setDragOverCell(null);
      ignoreNextClickRef.current = true;
    }
    if (isBoxDragging && containerRef.current) {
      // Always update the parent with the final mouse position before ending selection
      const rect = containerRef.current.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      const cellX = Math.floor((mouseX - containerSize.width / 2 - gridOffset.x) / (CELL_SIZE * zoom));
      const cellY = Math.floor((mouseY - containerSize.height / 2 - gridOffset.y) / (CELL_SIZE * zoom));
      // Update the box selection with the final position before ending
      onBoxSelectionUpdate && onBoxSelectionUpdate({ x: cellX, y: cellY });
      onBoxSelectionEnd && onBoxSelectionEnd({ x: cellX, y: cellY });
      ignoreNextClickRef.current = true;
    } else if (isBoxSelecting && !isBoxDragging) {
      // Simple click on empty space - don't ignore the click, let it go through to handleCanvasClick
      onBoxSelectionEnd && onBoxSelectionEnd({ x: boxDragStart.x, y: boxDragStart.y });
    }
    setIsBoxDragging(false);
    setBoxDragStart(null);
  };

  // Add global mouse event listeners
  useEffect(() => {
    if (isBoxSelecting || isBoxDragging || isDraggingSelection) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isBoxSelecting, isBoxDragging, isDraggingSelection, dragSelectionStart, gridOffset, zoom, containerSize]);

  useEffect(() => {
    if (onContainerSizeChange) {
      onContainerSizeChange(containerSize);
    }
  }, [containerSize, onContainerSizeChange]);

  // Helper: parse text into parts (text segments and symbol references in order)
  function parseTextAndSymbols(text) {
    if (!text) return { parts: [] };
    const cleanText = text
      .replace(/^\{\{(red|blue|grey|purple|green|yellow)\}\}\s*/i, '')
      .replace(/^\{\{background:\d+\}\}\s*/i, '');
    
    // Split by {{symbol:N}} while keeping the delimiters (allow flexible whitespace, case-insensitive)
    const parts = [];
    const regex = /\{\{\s*symbol\s*:\s*(\d+)\s*\}\}/gi;
    let lastIndex = 0;
    let match;
    
    while ((match = regex.exec(cleanText)) !== null) {
      // Text before this symbol
      if (match.index > lastIndex) {
        const textSegment = cleanText.substring(lastIndex, match.index);
        if (textSegment) {
          parts.push({ type: 'text', content: textSegment });
        }
      }
      // The symbol reference
      const symbolId = parseInt(match[1], 10);
      parts.push({ type: 'symbol', id: symbolId, raw: match[0] });
      lastIndex = regex.lastIndex;
    }
    
    // Remaining text after last symbol
    if (lastIndex < cleanText.length) {
      const textSegment = cleanText.substring(lastIndex);
      if (textSegment) {
        parts.push({ type: 'text', content: textSegment });
      }
    }
    
    return { parts };
  }

  // Helper: calculate appropriate font size based on text length and card dimensions
  function calculateFontSize(text, zoom, maxWidth, maxHeight) {
    if (!text) return 12 * zoom;
    
    const { parts } = parseTextAndSymbols(text);
    // Count characters excluding color prefix and symbol suffix
    const textContent = parts.filter(p => p.type === 'text').map(p => p.content).join('');
    const cleanText = textContent.replace(/\s+/g, ' ').trim();
    const charCount = cleanText.length;
    
    // Base font size
    let fontSize = 12 * zoom;
    
    // For text under 50 chars (excluding tags), use larger font (all caps style)
    // For longer text, scale down
    if (charCount <= 30) {
      fontSize = 16 * zoom;
    } else if (charCount <= 50) {
      fontSize = 13 * zoom;
    } else if (charCount <= 80) {
      fontSize = 12 * zoom;
    } else if (charCount <= 120) {
      fontSize = 11 * zoom;
    } else if (charCount <= 160) {
      fontSize = 10 * zoom;
    } else {
      fontSize = 9 * zoom;
    }
    
    // Minimum readable size
    return Math.max(6 * zoom, fontSize);
  }

  // Helper: render text with symbol references inline within the text flow
  function renderTextWithSymbols(text, sizePx = 32) {
    if (!text) return null;
    const { parts } = parseTextAndSymbols(text);
    
    // If only a single symbol reference (no text), render large centered
    if (parts.length === 1 && parts[0].type === 'symbol') {
      const symbolId = parts[0].id;
      const symbol = symbols.find(s => s.symbol_id === symbolId);
      if (symbol && symbol.symbol) {
        return (
          <img
            src={cachedImgSrc(symbol.symbol, imageCache)}
            alt={`Symbol ${symbolId}`}
            style={{ width: '80%', height: '80%', objectFit: 'contain', display: 'block', margin: 'auto' }}
            draggable={false}
          />
        );
      } else {
        return <span>{text}</span>;
      }
    }

    // Render text and symbols inline within a single span
    const elements = parts.map((part, idx) => {
      if (part.type === 'symbol') {
        const symbol = symbols.find(s => s.symbol_id === part.id);
        if (symbol && symbol.symbol) {
          return (
            <img
              key={`symbol-${idx}`}
              src={cachedImgSrc(symbol.symbol, imageCache)}
              alt={`Symbol ${part.id}`}
              style={{ 
                width: sizePx, 
                height: sizePx, 
                objectFit: 'contain', 
                display: 'inline-block', 
                verticalAlign: 'middle',
                margin: '0 2px'
              }}
              draggable={false}
            />
          );
        }
        // Fallback: render raw text if symbol not found
        return <span key={`symbol-fallback-${idx}`}>{part.raw}</span>;
      } else {
        // Text segment
        return (
          <span key={`text-${idx}`}>{part.content}</span>
        );
      }
    });

    return (
      <span style={{
        display: 'inline-flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        width: '100%',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        lineHeight: 1.2
      }}>
        {elements}
      </span>
    );
  }


  // Calculate SVG overlay size and offset
  const svgWidth = containerSize.width;
  const svgHeight = containerSize.height;
  const svgOffsetX = 0;
  const svgOffsetY = 0;

  // Helper: convert cell to canvas pixel coordinates (center of cell)
  function cellToCanvasXY(cellX, cellY) {
    return {
      x: svgWidth / 2 + gridOffset.x + cellX * CELL_SIZE * zoom + CELL_SIZE * zoom / 2,
      y: svgHeight / 2 + gridOffset.y + cellY * CELL_SIZE * zoom + CELL_SIZE * zoom / 2
    };
  }

  // Cell-based relation selection
  const handleCanvasClick = (e) => {
    if (ignoreNextClickRef.current) {
      ignoreNextClickRef.current = false;
      return;
    }
    // Don't handle canvas click if we clicked on a card (handled in onMouseDown)
    const rect = containerRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const cellX = Math.floor((mouseX - containerSize.width / 2 - gridOffset.x) / (CELL_SIZE * zoom));
    const cellY = Math.floor((mouseY - containerSize.height / 2 - gridOffset.y) / (CELL_SIZE * zoom));
    const clickedCard = gridMap[`${cellX},${cellY}`];
    if (clickedCard) {
      return;
    }
    if (!containerRef.current) return;

    // Check if we clicked on a card first (recheck in case of race condition)
    const clickedCard2 = gridMap[`${cellX},${cellY}`];
    if (clickedCard2) {
      return; // Don't check for relations if we clicked on a card
    }

    // Find relations passing through this cell
    const matches = relations.filter(rel =>
      rel.path && rel.path.some(pt => pt.cellX === cellX && pt.cellY === cellY)
    );
    if (matches.length === 1) {
      onRelationClick && onRelationClick(matches[0].id);
      setRelationPopup(null);
    } else if (matches.length > 1) {
      setRelationPopup({ x: mouseX, y: mouseY, matches });
    } else {
      setRelationPopup(null);
      onSelectCard && onSelectCard(null);
    }
  };

  // Prevent rendering during moves
  if (isMoveInProgress) {
    return (
      <div
        ref={containerRef}
        style={{
          width: '100%',
          height: '100%',
          position: 'relative',
          background: '#222',
          userSelect: 'none',
          overflow: 'hidden',
          cursor: 'grab',
          ...style,
        }}
        onMouseDown={handleMouseDown}
        onClick={handleCanvasClick}
      >
        {/* Return empty div to prevent any re-rendering */}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        background: 'transparent',
        userSelect: 'none',
        overflow: 'hidden',
        cursor: 'grab',
        ...style,
      }}
      onMouseDown={handleMouseDown}
      onClick={handleCanvasClick}
    >
      {/* SVG overlay for relation preview path and all saved relations */}
      <svg
        width={svgWidth}
        height={svgHeight}
        style={{ position: 'absolute', left: svgOffsetX, top: svgOffsetY, pointerEvents: 'auto', zIndex: 1 }}
      >
        {/* Render all saved relations as splines */}
        {relations && relations.map((rel, idx) => {
          if (!Array.isArray(rel.path)) {
            return null;
          }
          const pts = rel.path.map(({ cellX, cellY }) => cellToCanvasXY(cellX, cellY));
          if (pts.length < 2) return null;
          let d = `M ${pts[0].x} ${pts[0].y}`;
          for (let i = 1; i < pts.length; i++) {
            const p0 = pts[i - 1];
            const p1 = pts[i];
            const prev = pts[i - 2] || p0;
            const next = pts[i + 1] || p1;
            const c1x = p0.x + (p1.x - prev.x) / 6;
            const c1y = p0.y + (p1.y - prev.y) / 6;
            const c2x = p1.x - (next.x - p0.x) / 6;
            const c2y = p1.y - (next.y - p0.y) / 6;
            d += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p1.x} ${p1.y}`;
          }
          const isSelected = rel.id === selectedRelation;
          return (
            <g key={rel.id} style={{ cursor: 'pointer' }}>
              <path
                d={d}
                fill="none"
                stroke={rel.color}
                strokeWidth={isSelected ? 12 * zoom : 6 * zoom}
                opacity={isSelected ? 1 : 0.7}
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  filter: isSelected ? 'drop-shadow(0 0 12px #fff)' : undefined,
                  transition: 'filter 0.5s, stroke-width 0.5s, opacity 0.5s',
                  pointerEvents: 'none',
                }}
              />
              {/* Relation type label at midpoint */}
              {rel.type && pts.length > 1 && (() => {
                const midIdx = Math.floor(pts.length / 2);
                const midPt = pts[midIdx];
                return (
                  <text
                    x={midPt.x}
                    y={midPt.y - 8 * zoom}
                    textAnchor="middle"
                    alignmentBaseline="middle"
                    fontSize={16 * zoom}
                    fontWeight="bold"
                    fill={isSelected ? '#fff' : rel.color}
                    stroke="#222"
                    strokeWidth={isSelected ? 2 : 1}
                    paintOrder="stroke"
                    style={{
                      pointerEvents: 'none',
                      filter: isSelected ? 'drop-shadow(0 0 4px #fff)' : 'drop-shadow(0 0 2px #000)',
                      userSelect: 'none',
                    }}
                  >
                    {rel.type}
                  </text>
                );
              })()}
            </g>
          );
        })}
        {/* Preview path (drawn above saved relations) */}
        {relationPreviewPath && relationPreviewPath.length > 1 && (() => {
          const getPt = ({ cellX, cellY }) => cellToCanvasXY(cellX, cellY);
          const pts = relationPreviewPath.map(getPt);
          if (pts.length === 2) {
            return (
              <line
                x1={pts[0].x}
                y1={pts[0].y}
                x2={pts[1].x}
                y2={pts[1].y}
                stroke={relationPreviewColor}
                strokeWidth={6 * zoom}
                opacity={0.7}
                strokeLinecap="round"
              />
            );
          }
          let d = `M ${pts[0].x} ${pts[0].y}`;
          for (let i = 1; i < pts.length; i++) {
            const p0 = pts[i - 1];
            const p1 = pts[i];
            const prev = pts[i - 2] || p0;
            const next = pts[i + 1] || p1;
            const c1x = p0.x + (p1.x - prev.x) / 6;
            const c1y = p0.y + (p1.y - prev.y) / 6;
            const c2x = p1.x - (next.x - p0.x) / 6;
            const c2y = p1.y - (next.y - p0.y) / 6;
            d += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p1.x} ${p1.y}`;
          }
          return (
            <path
              d={d}
              fill="none"
              stroke={relationPreviewColor}
              strokeWidth={6 * zoom}
              opacity={0.7}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          );
        })()}
      </svg>
      {/* Render grid cells */}
      {cells.map(({ cellX, cellY, card }, idx) => {
        // Calculate pixel position based on gridOffset
        let left = containerSize.width / 2 + (cellX * CELL_SIZE * zoom) + gridOffset.x;
        let top = containerSize.height / 2 + (cellY * CELL_SIZE * zoom) + gridOffset.y;

        // If group dragging, apply offset to all selected cards (but not during backend moves)
        if (isDraggingSelection && card && selectedCardIds.includes(card.canvas_card_id) && !isMoveInProgress) {
          left += dragSelectionOffset.x;
          top += dragSelectionOffset.y;
        }

        const isDropTarget = isDraggingSelection && dragOverCell && dragOverCell.cellX === cellX && dragOverCell.cellY === cellY && !card;
        const showDropTarget = draggingCardId && !card;
        // Only render drop targets for empty cells when dragging, otherwise only render cards
        if (!card && !showDropTarget) {
          // Always render empty cells but control visibility with opacity
          const showGrid = isDraggingSelection && dragSelectionOffset.x !== 0 && dragSelectionOffset.y !== 0;
          return (
            <div
              key={`${cellX},${cellY}`}
              style={{
                position: 'absolute',
                left,
                top,
                width: CELL_SIZE * zoom,
                height: CELL_SIZE * zoom,
                border: '1px solid rgba(255, 255, 255, 0.05)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'grab',
                boxSizing: 'border-box',
                zIndex: 1,
                pointerEvents: 'none',
                borderRadius: 5 * zoom,
                opacity: showGrid ? 0.5 : 0.2, // Subtle grid
                transition: 'opacity 0.5s ease-in-out',
              }}
            />
          );
        }
        return (
          <div
            key={`${cellX},${cellY}`}
            style={{
              position: 'absolute',
              left,
              top,
              width: CELL_SIZE * zoom,
              height: CELL_SIZE * zoom,
              border: card ? 'none' : isDropTarget ? '2px solid #4a90e2' : 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: card ? 'pointer' : 'grab',
              boxSizing: 'border-box',
              zIndex: card && selectedCardIds.includes(card.canvas_card_id) ? 2 : 1,
              boxShadow: isDropTarget ? '0 0 0 3px rgba(99, 102, 241, 0.5)' : undefined,
              filter: card && selectedCardIds.includes(card.canvas_card_id) ? 'drop-shadow(0 0 20px rgba(99, 102, 241, 0.8))' : undefined,
              transition: 'box-shadow 0.5s, filter 0.5s',
              pointerEvents: showDropTarget ? 'auto' : card ? 'auto' : 'none',
              background: (showDropTarget ? 'rgba(0,0,0,0)' : undefined),
              borderRadius: 5 * zoom,
            }}
            onMouseDown={e => {
              if (e.button === 1) {
                // Middle mouse panning: let it bubble up to the container
                return;
              }
              if (e.button === 0 && card) {
                const rect = containerRef.current.getBoundingClientRect();
                const mouseX = e.clientX - rect.left;
                const mouseY = e.clientY - rect.top;
                const isCtrlClick = e.ctrlKey || e.metaKey;
                const isAltClick = e.altKey;

                // Handle Ctrl+click selection in mouse down
                if (isCtrlClick) {
                  onSelectCard && onSelectCard(card, true, false);
                  e.stopPropagation();
                  return; // Don't start dragging for Ctrl+click
                }

                if (isAltClick) {
                  onSelectCard && onSelectCard(card, false, true);
                } else if (!selectedCardIds.includes(card.canvas_card_id)) {
                  // Select only this card if not already selected
                  onSelectCard && onSelectCard(card, false, false);
                }
                // Start group drag (even if only one card is selected)
                setTimeout(() => {
                  setIsDraggingSelection(true);
                  setDragSelectionStart({ x: mouseX, y: mouseY });
                  setDragSelectionOffset({ x: 0, y: 0 });
                }, 0);
                e.stopPropagation();
              }
            }}

          >
            {card && (() => {
              const texts = (card.texts || []).filter(t => t.language === language);
              const optIdx = card.selected_option_index || selectedTextOption[card.canvas_card_id] || (texts[0]?.option_index ?? 1);
              const textObj = texts.find(t => t.option_index === optIdx) || texts[0];
              const rawText = textObj?.text || '';
              const colorTagMatch = rawText.match(/^\{\{(red|blue|grey|purple|green|yellow)\}\}/i);
              const colorTag = colorTagMatch ? colorTagMatch[1].toLowerCase() : null;
              const hasColorTag = !!colorTag;

              return (
                <div
                  style={{
                    width: CELL_SIZE * zoom,
                    height: CELL_SIZE * zoom,
                    background: 'rgba(0, 0, 0, 0.8)',
                    boxShadow: '0 10px 10px rgba(0, 0, 0, 0.9)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    flexDirection: 'column',
                    cursor: 'grab',
                    borderRadius: 5 * zoom,
                  }}
                >
                  {/* Background image layer with brightness filter for leaf cards */}
                  {/* In export mode (isExportMode=true, zoom=4): card div is 488px (122*4),
                      background PNGs are 488x488, rendered at 1:1 by the browser.
                      No zoom scaling calculations needed for backgrounds — only text/icons use zoom. */}
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background: `url(${cachedImgSrc(showImages ? (card.custom_background || card.default_background) : card.default_background, imageCache)}) center/cover no-repeat`,
                      filter: hasColorTag ? 'brightness(1.5) contrast(0.85) saturate(1.2)' : 'none',
                      zIndex: 0,
                    }}
                  />

                  {/* Color blend overlay layer */}
                  {hasColorTag && (
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        background: getHazeColor(colorTag),
                        mixBlendMode: 'color',
                        pointerEvents: 'none',
                        zIndex: 1,
                      }}
                    />
                  )}

                  {/* Card type symbol (top left) */}
                  {(() => {
                    if (card.deck_id != 0 && card.type_id != 0) {
                      const cardType = cardTypes.find(ct => Number(ct.type_id) === Number(card.type_id));
                      if (cardType && cardType.symbol_id) {
                        const symbol = symbols.find(s => s.symbol_id === cardType.symbol_id);
                        if (symbol && symbol.symbol) {
                          return (
                            <img
                              src={cachedImgSrc(symbol.symbol, imageCache)}
                              alt="Card Type Symbol"
                              style={{
                                position: 'absolute',
                                top: 2 * zoom,
                                left: 2 * zoom,
                                width: 32 * zoom,
                                height: 32 * zoom,
                                objectFit: 'contain',
                                opacity: 0.8,
                                zIndex: 3,
                              }}
                              draggable={false}
                            />
                          );
                        }
                      }
                      return null;
                    }
                    return null;
                  })()}
                  {/* Deck symbol (bottom right) */}
                  {(() => {
                    const deck = decks.find(d => Number(d.deck_id) === Number(card.deck_id));
                    const deckSymbolId = deck && deck.deck_symbol_id;
                    const symbol = deckSymbolId ? symbols.find(s => s.symbol_id === deckSymbolId) : null;
                    if (symbol && symbol.symbol) {
                      return (
                        <img
                          src={cachedImgSrc(symbol.symbol, imageCache)}
                          alt="Deck Symbol"
                          style={{
                            position: 'absolute',
                            bottom: 2 * zoom,
                            right: 2 * zoom,
                            width: 20 * zoom,
                            height: 20 * zoom,
                            objectFit: 'contain',
                            opacity: 0.6,
                            zIndex: 3,
                          }}
                          draggable={false}
                        />
                      );
                    }
                    return null;
                  })()}
                  {/* Show user entered text */}
                  {card && (card.deck_id === 0 && card.type_id === 0) ? (
                    card.user_text && card.user_text.trim() ? (
                      (() => {
                        const canvasRegex = /\{Canvas:(\d+)\}/;
                        const match = card.user_text.match(canvasRegex);
                        if (match) {
                          const canvasId = parseInt(match[1], 10);
                          return (
                            <CanvasPreview
                              canvasId={canvasId}
                              width={CELL_SIZE * zoom * 0.9}
                              height={CELL_SIZE * zoom * 0.9}
                              language={language}
                              symbols={symbols}
                              showImages={showImages}
                              zoom={zoom}
                              padding={0.5}
                            />
                          );
                        } else {
                          return (
                            <span style={{
                              color: '#222',
                              borderRadius: 2 * zoom,
                              padding: `${2 * zoom}px ${2 * zoom}px`,
                              fontWeight: 500,
                              fontSize: 12 * zoom,
                              textAlign: 'center',
                              maxWidth: '90%',
                              width: '90%',
                              height: '90%',
                              overflowWrap: 'break-word',
                              wordBreak: 'break-word',
                              whiteSpace: 'pre-line',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              zIndex: 4,
                              lineHeight: 1.2,
                              flex: 1,
                            }}>
                              {card.user_text}
                            </span>
                          );
                        }
                      })()
                    ) : (
                      (() => {
                        const symbolZero = symbols.find(s => s.symbol_id === 0);
                        return symbolZero && symbolZero.symbol ? (
                          <img
                            src={cachedImgSrc(symbolZero.symbol, imageCache)}
                            alt="Symbol 0"
                            style={{ width: '60%', height: '60%', objectFit: 'contain', display: 'block', margin: 'auto' }}
                            draggable={false}
                          />
                        ) : (
                          <span style={{ color: '#aaa' }}>[No text]</span>
                        );
                      })()
                    )
                  ) : (
                    <span style={{
                      color: '#222',
                      borderRadius: 2 * zoom,
                      padding: `${2 * zoom}px ${2 * zoom}px`,
                      fontWeight: 900,
                      fontSize: calculateFontSize(textObj?.text, zoom),
                      textAlign: 'center',
                      maxWidth: '90%',
                      width: '90%',
                      height: '90%',
                      overflowWrap: 'break-word',
                      wordBreak: 'break-word',
                      whiteSpace: 'pre-line',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 4,
                      lineHeight: 1.2,
                      flex: 1,
                      textShadow: '0 0 10px rgb(255, 255, 255), 0 0 5px rgb(255, 255, 255)',
                    }}>
                      {textObj ? renderTextWithSymbols(textObj.text, 18 * zoom) : ''}
                    </span>
                  )}
                </div>
              );
            })()}
          </div>
        );
      })}
      {/* Box selection overlay */}
      {isBoxDragging && boxSelectionStart && boxSelectionEnd && (
        <div
          style={{
            position: 'absolute',
            left: Math.min(boxSelectionStart.x, boxSelectionEnd.x) * CELL_SIZE * zoom + containerSize.width / 2 + gridOffset.x,
            top: Math.min(boxSelectionStart.y, boxSelectionEnd.y) * CELL_SIZE * zoom + containerSize.height / 2 + gridOffset.y,
            width: Math.max(1, Math.abs(boxSelectionEnd.x - boxSelectionStart.x) + 1) * CELL_SIZE * zoom,
            height: Math.max(1, Math.abs(boxSelectionEnd.y - boxSelectionStart.y) + 1) * CELL_SIZE * zoom,
            border: '2px solid #6366f1',
            backgroundColor: 'rgba(99, 102, 241, 0.1)',
            pointerEvents: 'none',
            zIndex: 10,
          }}
        />
      )}
      {/* Popup for multiple relations in a cell */}
      {relationPopup && (
        <div
          style={{
            position: 'absolute',
            left: relationPopup.x,
            top: relationPopup.y,
            background: 'rgba(30,30,30,0.98)',
            color: '#fff',
            borderRadius: 8,
            boxShadow: '0 2px 12px #000',
            padding: 8,
            zIndex: 1000,
            minWidth: 180,
          }}
        >
          <div style={{ marginBottom: 6, fontWeight: 'bold' }}>Select a relation:</div>
          {relationPopup.matches.map(rel => (
            <div
              key={rel.id}
              style={{
                padding: '4px 8px',
                margin: '2px 0',
                borderRadius: 4,
                background: rel.id === selectedRelation ? rel.color : 'rgba(255,255,255,0.08)',
                color: rel.id === selectedRelation ? '#222' : rel.color,
                cursor: 'pointer',
                fontWeight: rel.id === selectedRelation ? 'bold' : undefined,
              }}
              onClick={() => {
                onRelationClick && onRelationClick(rel.id);
                setRelationPopup(null);
              }}
            >
              {rel.type || '(no type)'}
            </div>
          ))}
          <div style={{ textAlign: 'right', marginTop: 4 }}>
            <button onClick={() => setRelationPopup(null)} style={{ color: '#fff', background: 'none', border: 'none', cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
} 